import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, Check } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface UPIQRModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  upiId: string;
  amount: number;
  orderId: string;
  merchantName?: string; // ✅ NEW: Merchant name
}

export const UPIQRModal: React.FC<UPIQRModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  upiId,
  amount,
  orderId,
  merchantName = 'Ruchira', // ✅ DEFAULT VALUE
}) => {
  const [copied, setCopied] = useState(false); // ✅ COPY STATE
  const [timeRemaining, setTimeRemaining] = useState(300); // ✅ 5 MIN TIMER

  // ✅ COUNTDOWN TIMER
  useEffect(() => {
    if (!isOpen) return;

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          onClose(); // Auto-close after 5 minutes
          toast.error('Payment session expired. Please try again.');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // ✅ UPI URL WITH MERCHANT NAME
  const upiUrl = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(merchantName)}&am=${amount}&cu=INR&tn=Order_${orderId}`;

  // ✅ FORMAT TIME
  const minutes = Math.floor(timeRemaining / 60);
  const seconds = timeRemaining % 60;
  const isTimeWarning = timeRemaining < 60;

  // ✅ COPY UPI ID TO CLIPBOARD
  const handleCopyUPI = async () => {
    try {
      await navigator.clipboard.writeText(upiId);
      setCopied(true);
      toast.success('UPI ID copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  // ✅ HANDLE OPEN UPI APP LINK
  const handleOpenUPIApp = () => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (isMobile) {
      window.location.href = upiUrl;
      toast.success('Opening UPI app...');
    } else {
      toast('Please scan the QR code or enter UPI ID manually');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 max-h-screen overflow-y-auto">
        {/* HEADER */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Scan to Pay with UPI
          </h2>
          <p className="text-sm text-gray-500">Quick & Secure Payment</p>
        </div>

        {/* TIMER - Shows when less than 1 minute */}
        {isTimeWarning && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-center">
            <p className="text-sm font-semibold text-red-700">
              ⏰ Session expires in {minutes}:{seconds.toString().padStart(2, '0')}
            </p>
          </div>
        )}

        {/* QR CODE */}
        <div className="bg-gray-50 p-4 rounded-lg inline-block w-full flex justify-center mb-6 border-2 border-gray-200">
          <div className="bg-white p-3 rounded-lg">
            <QRCodeSVG 
              value={upiUrl} 
              size={240}
              level="H"
              includeMargin={true}
            />
          </div>
        </div>

        {/* PAYMENT DETAILS */}
        <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-4 mb-4 border border-orange-200">
          <div className="space-y-3">
            {/* Amount - HIGHLIGHTED */}
            <div className="bg-white rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Amount to Pay</p>
              <p className="text-3xl font-bold text-orange-600">₹{amount.toFixed(2)}</p>
            </div>

            {/* UPI ID - COPYABLE */}
            <div>
              <p className="text-xs text-gray-600 uppercase tracking-wide font-semibold mb-2">
                UPI ID / VPA
              </p>
              <div className="flex items-center gap-2 bg-white p-3 rounded-lg">
                <span className="font-mono text-sm flex-1 break-all">{upiId}</span>
                <button
                  onClick={handleCopyUPI}
                  className="flex-shrink-0 p-2 hover:bg-gray-100 rounded-lg transition"
                  title="Copy UPI ID"
                >
                  {copied ? (
                    <Check size={16} className="text-green-600" />
                  ) : (
                    <Copy size={16} className="text-gray-600" />
                  )}
                </button>
              </div>
            </div>

            {/* Order ID */}
            <div>
              <p className="text-xs text-gray-600 uppercase tracking-wide font-semibold mb-1">
                Order ID
              </p>
              <p className="font-mono text-sm bg-white p-2 rounded text-gray-700">{orderId}</p>
            </div>
          </div>
        </div>

        {/* INSTRUCTIONS */}
        <div className="text-sm text-gray-700 mb-6 bg-blue-50 rounded-lg p-4 border border-blue-200">
          <p className="font-semibold mb-3 flex items-center gap-2">
            <span className="text-lg">📱</span>
            How to Pay:
          </p>
          <ol className="list-decimal list-inside space-y-2">
            <li>Open any UPI app (GPay, PhonePe, Paytm, etc.)</li>
            <li>Scan the QR code or paste UPI ID</li>
            <li>Verify amount: <strong>₹{amount.toFixed(2)}</strong></li>
            <li>Enter PIN & complete payment</li>
            <li>Click "Payment Done" below</li>
          </ol>
        </div>

        {/* ACTION BUTTONS */}
        <div className="space-y-3">
          {/* Primary: Payment Confirmed */}
          <button
            onClick={onConfirm}
            className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-4 rounded-lg font-bold hover:from-green-600 hover:to-green-700 transition-all active:scale-95 flex items-center justify-center gap-2 shadow-lg"
          >
            <span className="text-xl">✅</span>
            <span>Payment Completed</span>
          </button>

          {/* Secondary: Open UPI App (Mobile Only) */}
          {/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) && (
            <button
              onClick={handleOpenUPIApp}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-bold hover:bg-blue-700 transition-all active:scale-95 flex items-center justify-center gap-2"
            >
              <span className="text-xl">🔗</span>
              <span>Open UPI App</span>
            </button>
          )}

          {/* Tertiary: FOOD Fallback */}
          <button
            onClick={onClose}
            className="w-full bg-orange-500 text-white py-3 px-4 rounded-lg font-bold hover:bg-orange-600 transition-all active:scale-95 flex items-center justify-center gap-2"
          >
            <span className="text-xl">🍽️</span>
            <span>Pay with FOOD</span>
          </button>
        </div>

        {/* INFO NOTE */}
        <div className="text-xs text-gray-600 mt-4 bg-yellow-50 p-3 rounded-lg border border-yellow-200">
          <p className="font-semibold mb-1">💡 Quick Tips:</p>
          <ul className="space-y-1 list-disc list-inside">
            <li>Payment is instant & secure</li>
            <li>Your order starts preparation immediately</li>
            <li>Session expires in {minutes}:{seconds.toString().padStart(2, '0')}</li>
          </ul>
        </div>

        {/* SECURITY NOTE */}
        <div className="text-xs text-center text-gray-500 mt-4 p-3 border-t">
          <p>🔒 Secure UPI Payment • No card details needed</p>
        </div>
      </div>
    </div>
  );
};
